import { FunctionComponent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import JunoPage from "../../components/JunoPage/JunoPage";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { SearchableTable } from "@fyrstain/hl7-front-library";
import i18n from "i18next";
import { Location } from "fhir/r5";
import {
  getOfficialIdentifierValue,
  getReferenceValue,
} from "../../utils/fhirReferences";
import "./Locations.css";
import { useErrorNavigation } from "../../services/useErrorNavigation";

const Locations: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //    Search config         //
  //////////////////////////////

  const locationStatusOptions = [
    { value: "active", label: i18n.t("label.active") },
    { value: "suspended", label: i18n.t("label.suspended") },
    { value: "inactive", label: i18n.t("label.inactive") },
    {
      value: "entered-in-error",
      label: i18n.t("label.enteredinerror"),
    },
  ];

  const onDetails = useCallback(
    (id: string) => {
      navigate("/Location/" + id);
    },
    [navigate]
  );

  const onError = useErrorNavigation();

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage titleKey={i18n.t("title.locationlist")} needsLogin={true}>
      <div className="locations-table">
        <SearchableTable
          searchCriteriaProperties={{
            title: i18n.t("title.searchcriteria"),
            submitButtonLabel: i18n.t("button.search"),
            resetButtonLabel: i18n.t("button.reset"),
            language: i18n.t,
            fixedParameters: {
              _elements: "id,name,identifier,status,mode,type,managingOrganization,partOf",
              _sort: "name",
              _tag: "internal",
            },
            inputs: [
              {
                label: i18n.t("label.locationname"),
                type: "text",
                searchParamsName: "name:contains",
              },
              {
                label: i18n.t("label.identifier"),
                type: "text",
                searchParamsName: "identifier",
              },
              {
                label: i18n.t("label.managingorganization"),
                type: "text",
                searchParamsName: "organization.identifier",
              },
              {
                label: i18n.t("label.parentlocation"),
                type: "text",
                searchParamsName: "partof.identifier",
              },
              {
                label: i18n.t("label.locationstatus"),
                type: "select",
                placeholder: i18n.t("label.locationstatus"),
                searchParamsName: "status",
                options: locationStatusOptions,
              },
            ],
          }}
          paginatedTableProperties={{
            columns: [
              {
                header: i18n.t("label.locationname"),
                dataField: "name",
                width: "35%",
              },
              {
                header: i18n.t("label.identifier"),
                dataField: "identifier",
                width: "20%",
              },
              {
                header: i18n.t("label.locationstatus"),
                dataField: "status",
                width: "15%",
              },
              {
                header: i18n.t("label.managingorganization"),
                dataField: "managingOrganization",
                width: "18%",
              },
              {
                header: i18n.t("label.parentlocation"),
                dataField: "parentLocation",
                width: "14%",
              },
            ],
            action: [
              {
                icon: faEye,
                onClick: onDetails,
              },
            ],
            mapResourceToData: (resource: Location) => {
              const primaryIdentifier = getOfficialIdentifierValue(resource.identifier);

              return {
                id: resource.id ?? "N/A",
                name: resource.name ?? "N/A",
                identifier: primaryIdentifier || resource.id || "N/A",
                status: resource.status ?? "N/A",
                managingOrganization:
                  getReferenceValue(resource.managingOrganization) || "N/A",
                parentLocation: getReferenceValue(resource.partOf) || "N/A",
              };
            },
            searchProperties: {
              serverUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
              resourceType: "Location",
            },
            onError: onError,
          }}
        />
      </div>
    </JunoPage>
  );
};

export default Locations;
